import React, { useState, useEffect, useMemo } from "react";

const AdminModal = ({
    db,
    open,
    currentEmail,
    onClose,
    teachers,
    isAddingTeacher,
    deletingTeacherUid,
    onAddTeacher,
    onDeleteTeacher,
    makingAdminUid,
    onMakeAdmin,
    onImportCSV,
    editingTeacherUid,
    setEditingTeacherUid,
    savingTeacherUid,
    onUpdateTeacher,
}) => {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [password, setPassword] = useState("");
    const [editName, setEditName] = useState("");
    const [editSurname, setEditSurname] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    if (!open) return null;

    const filteredTeachers = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return teachers;

        return teachers.filter((teacher) => {
            const fullName = `${teacher.name} ${teacher.surname}`.toLowerCase();
            return (
                fullName.includes(query) ||
                teacher.email.toLowerCase().includes(query) ||
                teacher.name.toLowerCase().includes(query) ||
                teacher.surname.toLowerCase().includes(query)
            );
        });
    }, [teachers, searchQuery]);

    const displayTeachers = filteredTeachers.filter(
        (t) => t.email !== currentEmail,
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        const data = {
            email: email.trim(),
            name: name.trim(),
            surname: surname.trim(),
            password: password.trim(),
        };

        if (!data.email || !data.name || !data.surname || !data.password)
            return;

        onAddTeacher(data);
        setEmail("");
        setName("");
        setSurname("");
        setPassword("");
    };

    const startEditing = (teacher) => {
        setEditName(teacher.name);
        setEditSurname(teacher.surname);
        setEditingTeacherUid(teacher.uid);
    };

    const cancelEditing = () => {
        setEditingTeacherUid(null);
        setEditName("");
        setEditSurname("");
    };

    const saveEdit = (uid) => {
        onUpdateTeacher(uid, { name: editName, surname: editSurname });
    };

    const isSaving = (uid) => savingTeacherUid === uid;

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div
                className="admin-modal-container"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="admin-modal-header">
                    <h2>Gestión de Profesores</h2>
                    <button className="admin-modal-close" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="admin-modal-body">
                    <div
                        style={{
                            display: "flex",
                            gap: "0.5rem",
                            marginBottom: "1.25rem",
                        }}
                    >
                        <button
                            type="button"
                            className="btn-add-teacher"
                            style={{
                                background:
                                    "linear-gradient(135deg, #855cd6 0%, #6b4bb5 100%)",
                            }}
                            onClick={onImportCSV}
                        >
                            Importar desde CSV
                        </button>
                    </div>
                    <form className="add-teacher-row" onSubmit={handleSubmit}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Correo electrónico"
                            disabled={isAddingTeacher}
                            required
                        />
                        <br />
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nombre"
                            disabled={isAddingTeacher}
                            required
                        />
                        <input
                            type="text"
                            value={surname}
                            onChange={(e) => setSurname(e.target.value)}
                            placeholder="Apellidos"
                            disabled={isAddingTeacher}
                            required
                        />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Contraseña temporal"
                            disabled={isAddingTeacher}
                            required
                        />
                        <button
                            type="submit"
                            className="btn-add-teacher"
                            disabled={isAddingTeacher}
                        >
                            {isAddingTeacher ? (
                                <>
                                    <span className="spinner" />
                                    Añadiendo...
                                </>
                            ) : (
                                "Añadir profesor"
                            )}
                        </button>
                    </form>

                    <div className="search-row">
                        <div style={{ position: "relative", width: "280px" }}>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#94a3b8"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                }}
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Buscar..."
                                className="search-input"
                                title="Buscar por nombre, apellido o correo electrónico"
                            />
                            {searchQuery && (
                                <button
                                    className="search-clean"
                                    onClick={() => setSearchQuery("")}
                                    title="Limpiar filtro"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {displayTeachers.length === 0 ? (
                        <p className="empty-state">
                            {searchQuery.trim()
                                ? "No se encontraron profesores"
                                : "No hay profesores registrados."}
                        </p>
                    ) : (
                        <div className="teacher-list">
                            {teachers
                                .filter((t) => t.email !== currentEmail)
                                .map((teacher) => (
                                    <div
                                        className="teacher-card"
                                        key={teacher.email}
                                        style={{
                                            opacity:
                                                deletingTeacherUid ==
                                                teacher.email
                                                    ? 0.5
                                                    : 1,
                                            transition: "opacity 0.2s",
                                        }}
                                    >
                                        <div className="teacher-info">
                                            {editingTeacherUid ===
                                            teacher.uid ? (
                                                <div className="teacher-edit-row">
                                                    <input
                                                        type="text"
                                                        value={editName}
                                                        onChange={(e) =>
                                                            setEditName(
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="Nombre"
                                                        autoFocus
                                                        disabled={isSaving(
                                                            teacher.uid,
                                                        )}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                "Enter"
                                                            )
                                                                saveEdit(
                                                                    teacher.uid,
                                                                );
                                                            if (
                                                                e.key ===
                                                                "Escape"
                                                            )
                                                                cancelEditing();
                                                        }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={editSurname}
                                                        onChange={(e) =>
                                                            setEditSurname(
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="Apellidos"
                                                        autoFocus
                                                        disabled={isSaving(
                                                            teacher.uid,
                                                        )}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                "Enter"
                                                            )
                                                                saveEdit(
                                                                    teacher.uid,
                                                                );
                                                            if (
                                                                e.key ===
                                                                "Escape"
                                                            )
                                                                cancelEditing();
                                                        }}
                                                    />
                                                    <button
                                                        className="btn-edit-save"
                                                        onClick={() =>
                                                            saveEdit(
                                                                teacher.uid,
                                                            )
                                                        }
                                                        disabled={isSaving(
                                                            teacher.uid,
                                                        )}
                                                        title="Guardar"
                                                    >
                                                        {isSaving(
                                                            teacher.uid,
                                                        ) ? (
                                                            <span className="spinner-small" />
                                                        ) : (
                                                            "✓"
                                                        )}
                                                    </button>
                                                    <button
                                                        className="btn-edit-cancel"
                                                        onClick={cancelEditing}
                                                        disabled={isSaving(
                                                            teacher.uid,
                                                        )}
                                                        title="Cancelar"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="teacher-name">
                                                    {teacher.name}{" "}
                                                    {teacher.surname}{" "}
                                                    {teacher.isAdmin && (
                                                        <span
                                                            className="admin-badge"
                                                            title="Administrador"
                                                        >
                                                            Admin
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            <span className="teacher-email">
                                                {teacher.email}
                                            </span>
                                        </div>
                                        <div className="teacher-actions">
                                            {!teacher.isAdmin && (
                                                <button
                                                    className="btn-make-admin"
                                                    onClick={() =>
                                                        onMakeAdmin(
                                                            teacher.uid,
                                                            teacher.email,
                                                        )
                                                    }
                                                    disabled={
                                                        makingAdminUid ===
                                                        teacher.uid
                                                    }
                                                    title="Hacer administrador"
                                                >
                                                    {makingAdminUid ===
                                                    teacher.uid ? (
                                                        <span className="spinner-small" />
                                                    ) : (
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M5 16L3 5l5.5 5 3.5-7 3.5 7 5.5-5-2 11H5z" />
                                                            <path d="M4 21h16" />
                                                        </svg>
                                                    )}
                                                </button>
                                            )}
                                            {editingTeacherUid !==
                                                teacher.uid && (
                                                <button
                                                    className="btn-edit-teacher"
                                                    onClick={() =>
                                                        startEditing(teacher)
                                                    }
                                                    title="Editar profesor"
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            <button
                                                className="btn-delete-teacher"
                                                onClick={() =>
                                                    onDeleteTeacher(
                                                        teacher.email,
                                                    )
                                                }
                                                disabled={
                                                    deletingTeacherUid ===
                                                    teacher.email
                                                }
                                                title={
                                                    deletingTeacherUid ===
                                                    teacher.email
                                                        ? "Eliminando..."
                                                        : "Eliminar profesor"
                                                }
                                            >
                                                {deletingTeacherUid ===
                                                teacher.email ? (
                                                    <span className="spinner-small" />
                                                ) : (
                                                    <svg
                                                        width="18"
                                                        height="18"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <line
                                                            x1="10"
                                                            y1="11"
                                                            x2="10"
                                                            y2="17"
                                                        ></line>
                                                        <line
                                                            x1="14"
                                                            y1="11"
                                                            x2="14"
                                                            y2="17"
                                                        ></line>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminModal;
